/**
 * Interactive hub visit — back, title, steps, submit CTA, Framer center, pulse, Done after submit.
 *
 * Belongs here: Framer submit motion + interactive chrome. Owns useHubSubmit.
 * Does not belong here: cold restore (see HubSubmittedScreen); page visit latch.
 *
 * Centering: top/bottom flexGrow spacers only. Do not add layout animations on the card —
 * they fight HubPresence height collapse and the spacer springs (stutter).
 */
import { useEffect } from 'react'
import { motion } from 'framer-motion'
import BackButton from '../../../../components/ui-components/BackButton'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../analytics/expertReviewEvents'
import { expertReviewSafeAreaTopStyle } from '../shared/expertReviewSafeArea'
import type { StatusQuestionnaireApiResponse } from '../types/statusQuestionnaire.types'
import {
  HubDoneFooter,
  HubPresence,
  HubSubmitStyles,
  HUB_SPRING,
  HubSubmitPhase
} from './HubSubmit'
import { HubCard, runAfterCardSwipe, type HubPortfolioSummary } from './HubCard'
import { useHubSubmit } from './useHubSubmit'
import { areAllStepsCompleted, getHighlightedStepId } from './hubSteps'

export function HubInteractiveScreen({
  shouldSwipeCardIn,
  summary,
  statusData,
  onBack,
  onDone
}: {
  shouldSwipeCardIn: boolean
  summary: HubPortfolioSummary
  statusData: StatusQuestionnaireApiResponse | undefined
  onBack: () => void
  onDone: () => void
}) {
  const { phase: submitPhase, shouldPlayCardSubmitPulse, startSubmit, firstPlanDropTime } =
    useHubSubmit()
  const isPending = submitPhase === HubSubmitPhase.Pending
  // Same commit: back exits + Done enters (both HUB_SPRING)
  const showSubmittedChrome = submitPhase === HubSubmitPhase.Submitted

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.Hub }
    })
  }, [])

  useEffect(() => {
    if (!isPending) return
    const highlightedStepId = getHighlightedStepId(statusData)
    if (!highlightedStepId) return
    return runAfterCardSwipe(shouldSwipeCardIn, () => {
      document?.getElementById(highlightedStepId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    })
  }, [statusData, isPending, shouldSwipeCardIn])

  return (
    <div
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`
      }}
      className="relative flex h-screen flex-col overflow-hidden bg-white px-5"
    >
      <header className="relative z-10 shrink-0">
        <HubPresence show={!showSubmittedChrome}>
          <BackButton
            testLocator="allocation-plan-back"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: ExpertReviewScreen.Hub,
                  eventId: ExpertReviewEventIds.Back,
                  buttonLabel: 'Back'
                }
              })
              onBack()
            }}
          />
        </HubPresence>
      </header>

      {/* Spacers + card slot all spring flexGrow together — no instant flex-1↔shrink-0 swap.
          Pending: title scrolls with the card. Submitted: this column scrolls when the card + timeline are taller than the screen. */}
      <div
        className={`relative z-0 flex min-h-0 flex-1 flex-col ${isPending ? '' : 'overflow-y-auto no-scrollbar'}`}
      >
        <motion.div
          initial={false}
          animate={{ flexGrow: isPending ? 0 : 1 }}
          transition={HUB_SPRING}
          className="min-h-0"
        />

        <motion.div
          initial={false}
          animate={{ flexGrow: isPending ? 1 : 0 }}
          transition={HUB_SPRING}
          className={`min-h-0 ${isPending ? 'overflow-y-auto no-scrollbar' : ''}`}
        >
          <HubTitle submitPhase={submitPhase} />
          <HubCard
            shouldSwipeCardIn={shouldSwipeCardIn}
            userName={summary.userName}
            portfolioValue={summary.portfolioValue}
            fundCount={summary.fundCount}
            submitPhase={submitPhase}
            shouldPlayCardSubmitPulse={shouldPlayCardSubmitPulse}
            firstPlanDropTime={firstPlanDropTime}
          />
          {/* Clearance for the floating submit card */}
          {isPending ? <div className="h-[110px] shrink-0" aria-hidden /> : null}
        </motion.div>

        <motion.div
          initial={false}
          animate={{ flexGrow: isPending ? 0 : 1 }}
          transition={HUB_SPRING}
          className="min-h-0"
        />
      </div>

      <HubFooter
        submitPhase={submitPhase}
        statusData={statusData}
        onSubmit={() => startSubmit(areAllStepsCompleted(statusData))}
      />

      <HubDoneFooter show={showSubmittedChrome} onDone={onDone} />

      <HubSubmitStyles />
    </div>
  )
}

/** Title block — visible only while phase is pending. */
function HubTitle({ submitPhase }: { submitPhase: HubSubmitPhase }) {
  return (
    <HubPresence show={submitPhase === HubSubmitPhase.Pending}>
      <div className="pb-6">
        <h1
          className="pt-4 text-[28px] font-[500] leading-[120%] text-[#232323]"
          test-locator="allocation-plan-title"
        >
          Submit portfolio to get <br /> your allocation plan
        </h1>
        <p
          className="pt-2 text-[13px] font-[400] leading-[150%] text-[#6A6E6E] max-w-[323px]"
          test-locator="allocation-plan-description"
        >
          Please ensure that your inputs are final before submitting, as they cannot be changed later.
        </p>
      </div>
    </HubPresence>
  )
}

/** Submit CTA — floats over the list while phase is pending. */
function HubFooter({
  submitPhase,
  statusData,
  onSubmit
}: {
  submitPhase: HubSubmitPhase
  statusData: StatusQuestionnaireApiResponse | undefined
  onSubmit: () => void
}) {
  return (
    <div className="absolute inset-x-0 bottom-0 z-20">
      <HubPresence show={submitPhase === HubSubmitPhase.Pending}>
        <div
          className="bg-white px-5"
          style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
        >
          <div className="flex flex-col items-center gap-2 rounded-[16px] border border-[#F0F4F7] bg-white pt-2">
            <p className="text-center text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
              Our experts need up to 6 days to review
            </p>
            <PrimaryButton
              testLocator="allocation-plan-submit"
              label="Submit portfolio"
              onClick={() => {
                lightImpactVibrate()
                trackEvent({
                  eventName: EventNames.ButtonTap,
                  payload: {
                    ScreenName: ExpertReviewScreen.Hub,
                    eventId: ExpertReviewEventIds.HubSubmit,
                    buttonLabel: 'Submit portfolio'
                  }
                })
                onSubmit()
              }}
              isDisabled={!areAllStepsCompleted(statusData)}
              primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
            />
          </div>
        </div>
      </HubPresence>
    </div>
  )
}
